import useResizeObserver from '@/hooks/useResizeObserver/useResizeObserver';
import { useSafeLayoutEffect } from '@/hooks/useSafeLayoutEffect/useSafeLayoutEffect';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import Icon from '../Icon/Icon';
import Link from '../Link/Link';
import styles from './ShowroomNav.module.scss';
import { ShowroomNavItemProps, ShowroomNavProps } from './types';

const ShowroomNav = ({ children, className }: ShowroomNavProps) => {
  const [hasLeftPaddle, setHasLeftPaddle] = useState(false);
  const [hasRightPaddle, setHasRightPaddle] = useState(false);
  const ref = useRef<HTMLUListElement>(null);

  const scrollToItemAtBoundary = (
    boundary: 'left' | 'right',
    boundaryOffset: number = 32
  ) => {
    if (!ref.current) {
      return;
    }

    const leftBoundary = boundaryOffset;
    const rightBoundary = ref.current.clientWidth - boundaryOffset;

    const relevantBoundary = boundary === 'left' ? leftBoundary : rightBoundary;

    for (let i = 0; i < ref.current.children.length; i++) {
      const { left, right } = ref.current.children
        .item(i)!
        .getBoundingClientRect();

      if (left < relevantBoundary && right > relevantBoundary) {
        ref.current.scrollTo({
          behavior: 'smooth',
          left:
            boundary === 'left'
              ? Math.max(0, ref.current.scrollLeft - (rightBoundary - right))
              : Math.min(
                  ref.current.scrollWidth,
                  ref.current.scrollLeft + Math.abs(leftBoundary - left)
                ),
        });

        return;
      }
    }
  };

  const handleLeftPaddleClick = () => scrollToItemAtBoundary('left');
  const handleRightPaddleClick = () => scrollToItemAtBoundary('right');

  useSafeLayoutEffect(() => {
    if (!ref.current) {
      return;
    }

    const element = ref.current;

    if (element.scrollWidth > element.clientWidth) {
      setHasRightPaddle(true);
    }

    const handler = () => {
      setHasLeftPaddle((ref.current?.scrollLeft || 0) > 0);
      setHasRightPaddle(
        (ref.current?.scrollLeft || 0) + (ref.current?.clientWidth || 0) <
          (ref.current?.scrollWidth || 0)
      );
    };

    element.addEventListener('scroll', handler, { passive: true });

    return () => {
      element.removeEventListener('scroll', handler);
    };
  }, [ref]);

  useResizeObserver(ref, ([entry]) => {
    setHasRightPaddle(
      entry.target.scrollWidth > entry.target.clientWidth &&
        (ref.current?.scrollLeft || 0) + (ref.current?.clientWidth || 0) <
          (ref.current?.scrollWidth || 0)
    );
  });

  return (
    <nav className={clsx(styles.base, className)}>
      <button
        className={clsx(
          styles.paddle,
          styles.isLeft,
          !hasLeftPaddle && styles.isHidden
        )}
        onClick={handleLeftPaddleClick}
        aria-label="Zum vorherigen Element scrollen"
      >
        <Icon size="iconSize20" variant="action/chevron-left" />
      </button>

      <ul className={styles.items} ref={ref}>
        {children}
      </ul>

      <button
        className={clsx(
          styles.paddle,
          styles.isRight,
          !hasRightPaddle && styles.isHidden
        )}
        onClick={handleRightPaddleClick}
        aria-label="Zum nächsten Element scrollen"
      >
        <Icon size="iconSize20" variant="action/chevron-right" />
      </button>
    </nav>
  );
};

const ShowroomNavItem = ({ icon, title, to }: ShowroomNavItemProps) => {
  return (
    <li>
      <Link className={styles.item} to={to}>
        <div className={styles.icon}>{icon}</div>
        <div className={styles.title}>{title}</div>
      </Link>
    </li>
  );
};

ShowroomNav.Item = ShowroomNavItem;

export default ShowroomNav;
